/**
 * useUnlockProgression Hook
 *
 * Monitors company state and automatically unlocks locations when requirements are met.
 * Shows notifications when new locations become available.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useSpatialStore } from '../stores/spatialStore';
import { useCompanyStore } from '../stores/companyStore';
import { useProductStore } from '../stores/productStore';
import { useGameStore } from '../stores/gameStore';
import { useDynamicEvents } from '../stores/eventStore';
import { CampusLocation, UnlockRequirement } from '../types/spatial';
import { getLocationConfig } from '../config/locationConfig';
import { unlockProgressionService } from '../services/tycoon/unlockProgressionService';

export interface UseUnlockProgressionResult {
  /** Check if a location can be unlocked with current state */
  checkLocationUnlock: (location: CampusLocation) => {
    canUnlock: boolean;
    unmetRequirements: UnlockRequirement[];
  };
  /** Get progress percentage for a locked location */
  getUnlockProgress: (location: CampusLocation) => number;
  /** Manually trigger unlock check (for testing or forced refresh) */
  forceCheck: () => void;
}

/**
 * Hook that monitors game state and unlocks locations when requirements are met
 */
export function useUnlockProgression(): UseUnlockProgressionResult {
  const { unlockedLocations, unlockLocation } = useSpatialStore(
    useShallow((state) => ({
      unlockedLocations: state.unlockedLocations,
      unlockLocation: state.unlockLocation,
    }))
  );

  // Get company state
  const companyState = useCompanyStore(
    useShallow((state) => ({
      cash: state.finances?.cash ?? 0,
      reputation: state.metrics?.reputation ?? 0,
      employees: state.employees || [],
    }))
  );

  // Get products
  const products = useProductStore((state) => state.products);

  // Get game date from game store (not real date)
  const gameDate = useGameStore((state) => state.gameDate);

  // Get event system for notifications
  const { actions: { addEvent } } = useDynamicEvents();

  // Track what was unlocked to avoid duplicate notifications
  const previouslyUnlockedRef = useRef<Set<CampusLocation>>(
    new Set(unlockedLocations)
  );

  /**
   * Check for new unlocks and trigger them
   */
  const checkAndUnlock = useCallback(() => {
    // Build company state for unlock service using game date
    const fullCompanyState = {
      cash: companyState.cash,
      reputation: companyState.reputation,
      employees: companyState.employees,
      year: gameDate.year,
      month: gameDate.month,
      day: gameDate.day,
      marketShare: 0,
      dailyStats: { profit: 0, revenue: 0, costs: 0 },
    };

    // Check for new unlocks
    const newlyUnlocked = unlockProgressionService.checkAllUnlocks(
      fullCompanyState,
      products,
      unlockedLocations
    );

    // Unlock each new location and show notification
    for (const location of newlyUnlocked) {
      // Skip if we already notified about this
      if (previouslyUnlockedRef.current.has(location)) {
        continue;
      }

      // Unlock the location
      unlockLocation(location);
      previouslyUnlockedRef.current.add(location);

      // Get location info for notification
      const config = getLocationConfig(location);

      // Show notification event using actual game date
      addEvent({
        event: {
          id: `unlock-${location}-${Date.now()}`,
          type: 'systemic',
          title: 'New Location Unlocked!',
          description: `You can now access the ${config.name}. ${config.description}`,
          probability: 1,
          duration: 0,
          cooldown: 0,
          category: 'social',
          severity: 'low',
        },
        triggeredAt: new Date(),
        gameDate: { day: gameDate.day, month: gameDate.month, year: gameDate.year },
        gameMonth: (gameDate.year - 2007) * 12 + gameDate.month,
        status: 'resolved',
      });
    }
  }, [companyState, products, unlockedLocations, unlockLocation, addEvent, gameDate]);

  // Run unlock check when relevant state changes
  useEffect(() => {
    checkAndUnlock();
  }, [
    companyState.cash,
    companyState.reputation,
    companyState.employees?.length,
    products.length,
    checkAndUnlock,
  ]);

  // Update the ref when unlockedLocations changes externally (e.g., from save load)
  useEffect(() => {
    previouslyUnlockedRef.current = new Set(unlockedLocations);
  }, [unlockedLocations]);

  /**
   * Check if a specific location can be unlocked
   */
  const checkLocationUnlock = useCallback(
    (location: CampusLocation) => {
      const fullCompanyState = {
        cash: companyState.cash,
        reputation: companyState.reputation,
        employees: companyState.employees,
        year: gameDate.year,
        month: gameDate.month,
        day: gameDate.day,
        marketShare: 0,
        dailyStats: { profit: 0, revenue: 0, costs: 0 },
      };

      const result = unlockProgressionService.checkRequirements(
        location,
        fullCompanyState,
        products
      );

      return {
        canUnlock: result.met,
        unmetRequirements: result.unmetRequirements,
      };
    },
    [companyState, products, gameDate]
  );

  /**
   * Get progress percentage for a location
   */
  const getUnlockProgress = useCallback(
    (location: CampusLocation): number => {
      const fullCompanyState = {
        cash: companyState.cash,
        reputation: companyState.reputation,
        employees: companyState.employees,
        year: gameDate.year,
        month: gameDate.month,
        day: gameDate.day,
        marketShare: 0,
        dailyStats: { profit: 0, revenue: 0, costs: 0 },
      };

      const { progressPercent } = unlockProgressionService.getProgress(
        location,
        fullCompanyState,
        products
      );

      return progressPercent;
    },
    [companyState, products, gameDate]
  );

  return {
    checkLocationUnlock,
    getUnlockProgress,
    forceCheck: checkAndUnlock,
  };
}

export default useUnlockProgression;
